/**
 * StepClassification - Wizard Step 2: Vessel classification and operational parameters
 * Transforms BasicInfoTab classification content with wizard styling
 * Preserves all existing logic and field names for compatibility
 */

import React from 'react';
import { Shield, Globe, Anchor } from 'lucide-react';
import WizardSection from '../WizardSection';
import OperationsSection from './components/OperationsSection';
import { VESSEL_TYPE_LABELS, VESSEL_CATEGORY_LABELS, getAvailableVesselTypes, getVesselTypeLabel } from '../../../../../config/vesselTypes';
import * as AutoConfig from '../../tabs/utils/vesselAutoConfig';

// Certificate display name helper
const getCertificateDisplayName = (config) => {
  const certificateType = config.certificateType || config.vesselType;
  
  if (!certificateType) {
    return 'Not selected - please complete Step 1';
  }
  
  // Certificate-based display names
  const certificateNames = {
    // Base certificates
    'workboat': 'Standard Workboat Certificate',
    'light_duty_workboat': 'Light Duty Workboat Certificate',
    'pilot_boat_dedicated': 'Dedicated Pilot Boat Certificate', 
    'police_boat': 'Police Boat Certificate (WBC3 Annex 3)',
    'non_self_propelled': 'Non-Self-Propelled Workboat Certificate',
    
    // Single endorsements
    'workboat_towing_endorsed': 'Standard Workboat with Towing Endorsement',
    'workboat_pilot_endorsed': 'Standard Workboat with Pilot Boat Endorsement',
    'workboat_mgo_transfer_endorsed': 'Standard Workboat with MGO Transfer Endorsement',
    'workboat_high_speed_endorsed': 'Standard Workboat with High Speed Endorsement',
    
    // Multiple endorsements
    'workboat_towing_pilot': 'Standard Workboat with Towing & Pilot Boat Endorsements',
    'workboat_towing_mgo': 'Standard Workboat with Towing & MGO Transfer Endorsements',
    'workboat_pilot_high_speed': 'Standard Workboat with Pilot Boat & High Speed Endorsements',
    'workboat_multiple_custom': 'Standard Workboat with Multiple Endorsements'
  };
  
  // Return certificate name or fall back to legacy vessel type
  return certificateNames[certificateType] || 
         getVesselTypeLabel(certificateType, config.operatingStatus) ||
         'Unknown certificate type';
};

const StepClassification = ({ draftConfig, onUpdate }) => {
  const handleChange = (field, value) => {
    onUpdate({ [field]: value });
  };

  const handleVesselTypeChange = (value) => {
    AutoConfig.handleVesselTypeChange(value, draftConfig, onUpdate);
  };

  const handleCategoryChange = (value) => {
    AutoConfig.handleCategoryChange(value, draftConfig, onUpdate);
  };

  const handleOperatingHoursChange = (value) => {
    AutoConfig.handleOperatingHoursChange(value, draftConfig, onUpdate);
  };

  const handleOperatingStatusChange = (value) => {
    const newStatus = value;
    const currentStatus = draftConfig.operatingStatus;
    
    // Show warning when changing between seagoing and inland
    if (currentStatus && newStatus && currentStatus !== newStatus) {
      const isChangingToInland = newStatus === 'inland';
      const isChangingToSeagoing = newStatus === 'seagoing';
      
      if (isChangingToInland || isChangingToSeagoing) {
        // Check if current vessel type will become invalid
        const availableTypes = getAvailableVesselTypes(newStatus);
        const willResetVesselType = draftConfig.vesselType && !availableTypes.includes(draftConfig.vesselType);
        
        let message = isChangingToInland 
          ? "Changing to inland waterways will update maintenance requirements to use inland regulations (COP13, MSN 1823, AINA Code) instead of WBC3. Special operations and equipment will remain but regulatory references will change."
          : "Changing to seagoing will update maintenance requirements to use WBC3 regulations instead of inland waterways codes. Special operations and equipment will remain but regulatory references will change.";
        
        if (willResetVesselType) {
          message += `\n\n⚠️ Your current vessel type (${VESSEL_TYPE_LABELS[draftConfig.vesselType]}) is not available for ${newStatus} operations and will be reset.`;
        }
        
        message += "\n\nDo you want to continue?";
        
        if (!window.confirm(message)) {
          return; // Cancel the change
        }
        
        // Reset vessel type if it's not available in new operating status
        if (willResetVesselType) {
          onUpdate({ 
            operatingStatus: newStatus,
            vesselType: '',
            // Reset dependent fields
            operations: [],
            specialEquipment: []
          });
          return;
        }
      }
    }
    
    handleChange('operatingStatus', newStatus);
  };

  return (
    <WizardSection>
      {/* Step Introduction */}
      <div className="text-center mb-6">
        <div className="mx-auto flex items-center justify-center h-14 w-14 md:h-12 md:w-12 rounded-full bg-blue-100 mb-3">
          <Shield className="h-7 w-7 md:h-6 md:w-6 text-blue-600" />
        </div>
        <h2 className="text-xl md:text-xl font-bold text-gray-900 mb-2">
          Classification
        </h2>
        <WizardSection.Text className="max-w-2xl mx-auto">
          Classify your vessel according to UK regulations. This determines which safety requirements and operational limits apply.
        </WizardSection.Text>
      </div>

      {/* Regulatory Context */}
      <WizardSection.Callout variant="regulatory" title="WBC3 Classification">
        Vessel classification determines equipment requirements, manning standards, and operational limits. 
        Seagoing vessels follow UK Workboat Code 3, while inland vessels follow different regulatory frameworks.
      </WizardSection.Callout>

      {/* Operating Status */}
      <WizardSection.Card>
        <WizardSection.Header>Operating Status</WizardSection.Header>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <WizardSection.Field
            label="Vessel Operating Status *"
            help="Select based on vessel certification and operating area"
          >
            <WizardSection.Select
              value={draftConfig.operatingStatus || ''}
              onChange={(e) => handleOperatingStatusChange(e.target.value)}
            >
              <option value="">Select status...</option>
              <option value="seagoing">Seagoing Vessel</option>
              <option value="inland">Inland Waterway Vessel</option>
            </WizardSection.Select>
          </WizardSection.Field>

          <WizardSection.Field
            label="Certificate Type"
            help="Set in Step 1 - Vessel Identity"
          >
            <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-700">
              {getCertificateDisplayName(draftConfig)}
            </div>
            {!draftConfig.certificateType && !draftConfig.vesselType && (
              <p className="mt-1 text-xs text-amber-600">
                ⚠️ Please go back to Step 1 to select your certificate type first
              </p>
            )}
          </WizardSection.Field>
        </div>
      </WizardSection.Card>

      {/* WBC3 Category (Seagoing only) */}
      {draftConfig.operatingStatus === 'seagoing' && (
        <WizardSection.Card>
          <WizardSection.Header>WBC3 Category & Operations</WizardSection.Header>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <WizardSection.Field
              label="WBC3 Category *"
              help="Determines operational limits and equipment requirements"
            >
              <WizardSection.Select
                value={draftConfig.category || ''}
                onChange={(e) => handleCategoryChange(e.target.value)}
              >
                <option value="">Select category...</option>
                {Object.entries(VESSEL_CATEGORY_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </WizardSection.Select>
            </WizardSection.Field>

            <WizardSection.Field
              label="Operating Hours"
              help="Affects manning requirements"
            >
              <WizardSection.Select
                value={draftConfig.operatingHours || 'under_24h'}
                onChange={(e) => handleOperatingHoursChange(e.target.value)}
              >
                <option value="under_24h">Under 24 hours</option>
                <option value="over_24h">Over 24 hours</option>
              </WizardSection.Select>
            </WizardSection.Field>
          </div>

          {/* GMDSS Sea Area */}
          <div className="mt-4">
            <WizardSection.Field
              label="GMDSS Sea Area *"
              help="Determines mandatory communication equipment per WBC3 Table 17.2.1"
            >
              <WizardSection.Select
                value={draftConfig.gmdssSeaArea || ''}
                onChange={(e) => {
                  const area = e.target.value;
                  // Update the GMDSS sea area field for matrix logic
                  onUpdate({
                    gmdssSeaArea: area,
                    // Legacy fields for compatibility
                    seaArea: area,
                    seaAreaA1: true, // All vessels operate in A1
                    seaAreaA2: area === 'A1+A2' || area === 'A1+A2+A3' || area === 'A1+A2+A3+A4',
                    seaAreaA3: area === 'A1+A2+A3' || area === 'A1+A2+A3+A4',
                    seaAreaA4: area === 'A1+A2+A3+A4'
                  });
                }}
              >
                <option value="">Select GMDSS sea area...</option>
                <option value="A1">A1 - VHF coverage from shore (coastal waters)</option>
                <option value="A1+A2">A1+A2 - MF coverage (up to ~150nm from coast)</option>
                <option value="A1+A2+A3">A1+A2+A3 - Satellite coverage (global except polar)</option>
                <option value="A1+A2+A3+A4">A1+A2+A3+A4 - Global including polar regions</option>
              </WizardSection.Select>
            </WizardSection.Field>
          </div>

          {/* FT-30 Part 3: Stability Information Booklet toggle */}
          <div className="mt-4">
            <label className="flex items-start space-x-3">
              <input
                type="checkbox"
                checked={draftConfig.hasStabilityBooklet || false}
                onChange={(e) => handleChange('hasStabilityBooklet', e.target.checked)}
                className="h-4 w-4 text-cyan-600 focus:ring-cyan-600 border-gray-300 rounded mt-1"
              />
              <div>
                <span className="text-sm font-medium text-gray-900">Vessel requires Stability Information Booklet (SIB)</span>
                <p className="text-xs text-gray-500 mt-1">
                  A SIB is required if any of these apply: operating in Category 0 or 1 · carrying 16+ persons · cargo over 1,000 kg ·
                  cargo with free surface effect · fitted with lifting devices (excluding person retrieval/anchor handling/tender davits) ·
                  towing where towed object is &gt;2× vessel displacement · seagoing pilot boat · operating in icing areas.
                  <strong> WBC3 Section 12.1.1.3</strong>
                </p>
                <p className="text-xs text-amber-600 mt-1 font-medium">
                  If checked, the Master will require a 1-day MCA-approved stability course per WBC3 Table A5.3.
                </p>
              </div>
            </label>
          </div>

          {/* FT-158: Single-Handed Operations toggle (Cat 3-6 only) */}
          {(draftConfig.category === 'cat3' || draftConfig.category === 'cat4' ||
            draftConfig.category === 'cat5' || draftConfig.category === 'cat6') && (
            <div className="mt-4">
              <label className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  checked={draftConfig.singleHandedEndorsed || false}
                  onChange={(e) => handleChange('singleHandedEndorsed', e.target.checked)}
                  className="h-4 w-4 text-cyan-600 focus:ring-cyan-600 border-gray-300 rounded mt-1"
                />
                <div>
                  <span className="text-sm font-medium text-gray-900">Single-Handed Operations Permitted</span>
                  <p className="text-xs text-gray-500 mt-1">
                    Vessel and Master are endorsed for single-handed operation. Only applicable to Category 3-6 vessels
                    where the vessel is suitable and the Master holds appropriate endorsement.
                    <strong> WBC3 Table A5.1</strong>
                  </p>
                  <p className="text-xs text-amber-600 mt-1 font-medium">
                    If checked, minimum crew will be 1 (Master only) instead of 2.
                  </p>
                </div>
              </label>
            </div>
          )}
        </WizardSection.Card>
      )}

      {/* Inland Water Category */}
      {draftConfig.operatingStatus === 'inland' && (
        <WizardSection.Card>
          <WizardSection.Header>Inland Water Classification</WizardSection.Header>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <WizardSection.Field
              label="Water Category *"
              help="As per MSN 1837 categorisation"
            >
              <WizardSection.Select
                value={draftConfig.waterCategory || ''}
                onChange={(e) => handleChange('waterCategory', e.target.value)}
              >
                <option value="">Select category...</option>
                <option value="A">Category A - Narrow rivers/canals (&lt;1.5m depth)</option>
                <option value="B">Category B - Wider waters (≥1.5m depth, waves &lt;0.6m)</option>
                <option value="C">Category C - Tidal waters (waves &lt;1.2m)</option>
                <option value="D">Category D - Tidal waters (waves &lt;2.0m)</option>
              </WizardSection.Select>
            </WizardSection.Field>

            <WizardSection.Field
              label="Operating Hours"
              help="Affects crew rest requirements"
            >
              <WizardSection.Select
                value={draftConfig.operatingHours || 'under_24h'}
                onChange={(e) => handleOperatingHoursChange(e.target.value)}
              >
                <option value="under_24h">Under 24 hours</option>
                <option value="over_24h">Over 24 hours</option>
              </WizardSection.Select>
            </WizardSection.Field>
          </div>
        </WizardSection.Card>
      )}

      {/* Inland Operations (if applicable) */}
      {draftConfig.operatingStatus === 'inland' && (
        <WizardSection.Card>
          <WizardSection.Header>Inland Operations</WizardSection.Header>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={draftConfig.carriesVulnerablePassengers || false}
                  onChange={(e) => handleChange('carriesVulnerablePassengers', e.target.checked)}
                  className="h-4 w-4 text-cyan-600 focus:ring-cyan-600 border-gray-300 rounded"
                />
                <div>
                  <span className="text-sm font-medium text-gray-900">Carries Vulnerable Passengers</span>
                  <p className="text-xs text-gray-500">Affects lifejacket requirements in Cat A/B waters</p>
                </div>
              </label>
            </div>

            <div>
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={draftConfig.operatesAtNight || false}
                  onChange={(e) => handleChange('operatesAtNight', e.target.checked)}
                  className="h-4 w-4 text-cyan-600 focus:ring-cyan-600 border-gray-300 rounded"
                />
                <div>
                  <span className="text-sm font-medium text-gray-900">Night Operations</span>
                  <p className="text-xs text-gray-500">Requires lights on lifejackets per COP13 13.2.4</p>
                </div>
              </label>
            </div>

            <div>
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={draftConfig.allPersonsWearLifejackets || false}
                  onChange={(e) => handleChange('allPersonsWearLifejackets', e.target.checked)}
                  className="h-4 w-4 text-cyan-600 focus:ring-cyan-600 border-gray-300 rounded"
                />
                <div>
                  <span className="text-sm font-medium text-gray-900">All Persons Wear Lifejackets</span>
                  <p className="text-xs text-gray-500">Exempts lifebuoy requirement per COP13 13.1.4</p>
                </div>
              </label>
            </div>

            <div>
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={draftConfig.hasEnclosedBridge || false}
                  onChange={(e) => handleChange('hasEnclosedBridge', e.target.checked)}
                  className="h-4 w-4 text-cyan-600 focus:ring-cyan-600 border-gray-300 rounded"
                />
                <div>
                  <span className="text-sm font-medium text-gray-900">Enclosed Bridge/Wheelhouse</span>
                  <p className="text-xs text-gray-500">Requires sound reception equipment in Cat 0-2 per WBC3 19.8</p>
                </div>
              </label>
            </div>
          </div>
        </WizardSection.Card>
      )}

      {/* Hire Boat Regulatory Path (if applicable) */}
      {draftConfig.operatingStatus === 'inland' && draftConfig.vesselType === 'hire_self_drive' && (
        <WizardSection.Card>
          <WizardSection.Header>Regulatory Compliance Path</WizardSection.Header>
          
          <WizardSection.Field
            label="Choose Regulatory Framework *"
            help="Select the appropriate compliance framework for hire boats"
          >
            <WizardSection.Select
              value={draftConfig.inlandRegulatoryPath || 'hire_boat_code'}
              onChange={(e) => handleChange('inlandRegulatoryPath', e.target.value)}
            >
              <option value="hire_boat_code">AINA Hire Boat Code (Recommended)</option>
              <option value="class_ix_a">Full Class IX(A) Regulations</option>
            </WizardSection.Select>
          </WizardSection.Field>

          <div className="mt-3">
            {draftConfig.inlandRegulatoryPath === 'hire_boat_code' ? (
              <WizardSection.Callout variant="success" title="AINA Hire Boat Code 2021 (General Exemption)">
                • Practical safety standards for hire boats<br/>
                • Risk assessments and safety equipment required<br/>
                • General exemption from full Class IX(A) requirements<br/>
                • Turn-round checks every hire period<br/>
                • Weekly periodic inspections<br/>
                • Annual major servicing requirements
              </WizardSection.Callout>
            ) : (
              <WizardSection.Callout variant="warning" title="Full Class IX(A) Regulations">
                • Complete merchant shipping LSA requirements (SI 2721/1999)<br/>
                • Full fire fighting equipment requirements (SI 1011/1998)<br/>
                • MCA inspection powers apply<br/>
                • Higher compliance burden but comprehensive
              </WizardSection.Callout>
            )}
          </div>
        </WizardSection.Card>
      )}

      {/* MOB Equipment Configuration - For Pilot and Police Boats */}
      {(draftConfig.vesselType === 'pilot' || draftConfig.vesselType === 'police') && draftConfig.operatingStatus === 'seagoing' && (
        <WizardSection.Card>
          <WizardSection.Header>
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-orange-600" />
              MOB Equipment Configuration
            </div>
          </WizardSection.Header>
          
          <WizardSection.Text>
            Additional configuration for vessel-specific MOB recovery requirements.
          </WizardSection.Text>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {/* Pilot Boat Configuration */}
            {draftConfig.vesselType === 'pilot' && (
              <div>
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={draftConfig.hasGuardrails || false}
                    onChange={(e) => handleChange('hasGuardrails', e.target.checked)}
                    className="h-4 w-4 text-cyan-600 focus:ring-cyan-600 border-gray-300 rounded"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-900">Proper Guardrails/Bulwarks</span>
                    <p className="text-xs text-gray-500">Does vessel have proper guardrails or bulwarks? (affects fall prevention requirements per WBC3 22.2.3)</p>
                  </div>
                </label>
              </div>
            )}

            {/* Police Boat Configuration */}
            {draftConfig.vesselType === 'police' && (
              <>
                <div>
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={draftConfig.lowFreeboard || false}
                      onChange={(e) => handleChange('lowFreeboard', e.target.checked)}
                      className="h-4 w-4 text-cyan-600 focus:ring-cyan-600 border-gray-300 rounded"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-900">Low Freeboard</span>
                      <p className="text-xs text-gray-500">Does vessel have low freeboard making recovery difficult? (affects recovery system requirements per WBC3 Annex 3 20.4.2)</p>
                    </div>
                  </label>
                </div>

                <div>
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={draftConfig.hasDrySuits || false}
                      onChange={(e) => handleChange('hasDrySuits', e.target.checked)}
                      className="h-4 w-4 text-cyan-600 focus:ring-cyan-600 border-gray-300 rounded"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-900">Dry Suits Provided</span>
                      <p className="text-xs text-gray-500">Are dry suits provided for all crew? (affects some equipment requirements)</p>
                    </div>
                  </label>
                </div>
              </>
            )}
          </div>

          {/* Regulatory Context */}
          <WizardSection.Callout variant="regulatory" title="WBC3 MOB Requirements Context">
            {draftConfig.vesselType === 'pilot' && (
              <div>
                <p className="text-sm"><strong>Pilot Boats (WBC3 Table 27.2.3):</strong></p>
                <ul className="text-sm mt-2 space-y-1">
                  <li>• Enhanced MOB recovery equipment required for person transfer operations</li>
                  <li>• Additional fall prevention required when guardrails not fitted (WBC3 22.2.3)</li>
                  <li>• Specialized equipment for night and adverse weather recovery</li>
                </ul>
              </div>
            )}
            {draftConfig.vesselType === 'police' && (
              <div>
                <p className="text-sm"><strong>Police Boats (WBC3 Annex 3):</strong></p>
                <ul className="text-sm mt-2 space-y-1">
                  <li>• Enhanced unconscious person recovery capability required (15.3.1)</li>
                  <li>• Additional recovery systems for low freeboard vessels (20.4.2)</li>
                  <li>• Enforcement scenario recovery equipment (20.4.1)</li>
                </ul>
              </div>
            )}
          </WizardSection.Callout>
        </WizardSection.Card>
      )}

      {/* Operations Configuration - Critical for WBC3 Equipment Requirements */}
      <OperationsSection draftConfig={draftConfig} onUpdate={onUpdate} />

      {/* Help Information */}
      <WizardSection.Callout variant="info" title="What's Next?">
        After classification and operations setup, we'll configure your vessel's propulsion system and fire protection requirements 
        based on your operational profile and special operations.
      </WizardSection.Callout>
    </WizardSection>
  );
};

export default StepClassification;